/**
 * In-memory access token store.
 *
 * SECURITY: the token is held in a module-scoped variable and read per-request
 * by apiClient — never captured into a header at module load. (The legacy app
 * does the opposite: `let token = Cookies.get('jwt_token')` runs once at import,
 * so a token obtained later in the same page life is invisible to axios, which
 * is why its login ends in `window.location.reload()`. Nothing here needs a
 * reload.)
 *
 * Memory is the source of truth. It is MIRRORED to the legacy *.amd.com cookie
 * set so a refresh does not end the session and so other AMD properties see the
 * same session — see sessionMirror.js for the full trade, including what that
 * domain scope costs. Nothing is written to localStorage.
 */
import { isTokenExpired } from './jwt';
import { clearSession, loadSession, patchSession } from './sessionMirror';

let accessToken = null;
let tokenExpiresAt = 0; // epoch ms
const subscribers = new Set();

export function setAccessToken(token, expiresInSeconds) {
  accessToken = token;
  tokenExpiresAt = expiresInSeconds ? Date.now() + expiresInSeconds * 1000 : 0;
  // patchSession, not saveSession: the identity half of the mirror is written
  // by the login flow and must not be clobbered by a token refresh.
  patchSession({ token });
  subscribers.forEach((fn) => fn(Boolean(token)));
}

export function getAccessToken() {
  return accessToken;
}

/**
 * Held and not past its `exp`. The token itself is the judge (jwt.isTokenExpired):
 * `tokenExpiresAt` is 0 — "no stated expiry" — for a token that was already
 * expired when stored, e.g. one restored from the cookies after its hour, so on
 * its own it would pass that token.
 */
export function hasValidToken() {
  if (!accessToken || isTokenExpired(accessToken)) return false;
  return !tokenExpiresAt || Date.now() < tokenExpiresAt;
}

export function clearAccessToken() {
  accessToken = null;
  tokenExpiresAt = 0;
  clearSession();
  subscribers.forEach((fn) => fn(false));
}

/**
 * Re-seed memory from the session mirror after a reload.
 *
 * Returns the mirrored identity so bootstrapAuth can restore Redux in the same
 * step — the token and the identity have to come back together, or the guard
 * lets you in with no user to render.
 *
 * Roles are NOT resolved here. The mirror holds the server's full { CCA, EIA }
 * matrix, and which branch applies depends on the variant being served, so the
 * caller resolves it — see bootstrapAuth.js.
 *
 * @returns {{ user: object, featuresData: object|null } | null}
 */
export function restoreSession() {
  const session = loadSession();
  if (!session) return null;

  accessToken = session.token;
  tokenExpiresAt = session.expiresAt ?? 0;
  subscribers.forEach((fn) => fn(true));

  return { user: session.user, featuresData: session.featuresData ?? null };
}

/** Subscribe to auth changes; returns an unsubscribe fn. */
export function onAuthChange(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}
